import { useEffect, useState } from 'react';
import type { ResolvedCardTheme } from './cardTheme';
import type { CardPrintField } from './cardLayout';
import { getCardPostBadgeLabel, type CardPrintPost } from './useCardPrintPosts';
import { resolveCategoryBadgeStyle } from './builtinFields';
import { generateQrDataUrl } from './QrCodeOverlay';
import {
  buildFieldRuns,
  computeBadgeBox,
  computeLogoBox,
  computePhotoBox,
  computeQrBox,
  computeTextBox,
  IN_TO_PX,
  MM_PER_IN,
  pptxColorToCss,
  slideBadgeFontPt,
  SLIDE_H_IN,
  SLIDE_W_IN,
} from './slideLayout';

/**
 * PowerPoint (16:9 / 1 投稿 1 スライド) の画面プレビュー。
 *
 * `slideLayout.ts` の純レイアウト (buildCardPptx と共有) を CSS px に変換して描画し、
 * 実際に書き出す .pptx と見た目を一致させる (WYSIWYG)。写真は object-fit:contain で
 * 写真全体が必ず収まる (letterbox = theme.backgroundColor)。pptxgenjs には依存しない。
 *
 * スライドは自然寸法 (13.333×7.5 inch × 96dpi = 1280×720px) で描画し、親側が transform:scale で
 * プレビュー幅に合わせる。
 */

export const SLIDE_W_PX = SLIDE_W_IN * IN_TO_PX;
export const SLIDE_H_PX = SLIDE_H_IN * IN_TO_PX;
/** pt → CSS px (96dpi)。slideLayout の fontSize は pt。 */
const PT_TO_PX = 96 / 72;

export interface SlidePreviewProps {
  post: CardPrintPost;
  fields: CardPrintField[];
  theme: ResolvedCardTheme;
  adminNote: string;
  labelMap: Record<string, string>;
  codeToName: Record<string, string>;
  sessionNameMap: Record<string, string>;
  /** 地図リンク URL (QR encode 対象)。未指定/空は QR なし。 */
  qrValue?: string;
  /** 表示に使う写真 URL (実写真 or サンプル画像)。null/undefined は写真なし。 */
  resolvedPhotoUrl?: string | null;
}

export function SlidePreview({
  post,
  fields,
  theme,
  adminNote,
  labelMap,
  codeToName,
  sessionNameMap,
  qrValue,
  resolvedPhotoUrl,
}: SlidePreviewProps): JSX.Element {
  const px = (inch: number): number => inch * IN_TO_PX;
  const hasQr = !!qrValue;
  const photo = computePhotoBox(theme);
  const textBox = computeTextBox(theme, hasQr);
  const runs = buildFieldRuns(post, fields, theme, {
    adminNote,
    labelMap,
    codeToName,
    sessionNameMap,
  });

  const catField = fields.find((f) => f.key === 'category');
  const catLabel = catField ? getCardPostBadgeLabel(post, catField.fieldPath) : '';
  const badgeStyle = resolveCategoryBadgeStyle(catField);
  const badgeFontPt = slideBadgeFontPt(badgeStyle.fontSize);
  const badge = catLabel ? computeBadgeBox(theme, catLabel, badgeFontPt) : null;
  const badgeColor = catLabel ? (theme.categoryColors[catLabel] ?? theme.accent) : theme.accent;

  const qr = computeQrBox();
  const logo = computeLogoBox();
  const hasLogo = !!theme.logoUrl && /^https?:\/\//.test(theme.logoUrl);

  const photoSrc =
    resolvedPhotoUrl !== undefined && resolvedPhotoUrl !== null ? resolvedPhotoUrl : null;

  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (!qrValue) {
      setQrDataUrl(null);
      return;
    }
    void generateQrDataUrl(qrValue, {
      darkColor: theme.textColor,
      lightColor: theme.backgroundColor,
      widthPx: 256,
    }).then((url) => {
      if (!cancelled) setQrDataUrl(url);
    });
    return () => {
      cancelled = true;
    };
  }, [qrValue, theme.textColor, theme.backgroundColor]);

  return (
    <div
      className="slide-preview"
      style={{
        position: 'relative',
        width: `${SLIDE_W_PX}px`,
        height: `${SLIDE_H_PX}px`,
        background: theme.backgroundColor,
        overflow: 'hidden',
        boxSizing: 'border-box',
        fontFamily: "'Yu Gothic', 'Hiragino Kaku Gothic ProN', sans-serif",
        border:
          theme.border.width_mm > 0
            ? `${(theme.border.width_mm / MM_PER_IN) * IN_TO_PX}px solid ${theme.border.color}`
            : undefined,
      }}
    >
      {/* 写真 (contain — 写真全体が収まり、余白は背景色で letterbox) */}
      <div
        style={{
          position: 'absolute',
          left: `${px(photo.x)}px`,
          top: `${px(photo.y)}px`,
          width: `${px(photo.w)}px`,
          height: `${px(photo.h)}px`,
          background: theme.backgroundColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {photoSrc ? (
          <img
            src={photoSrc}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        ) : (
          <span style={{ fontSize: '18px', color: '#94a3b8' }}>(写真なし)</span>
        )}
      </div>

      {/* テキスト項目 */}
      <div
        style={{
          position: 'absolute',
          left: `${px(textBox.x)}px`,
          top: `${px(textBox.y)}px`,
          width: `${px(textBox.w)}px`,
          height: `${px(textBox.h)}px`,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          overflow: 'hidden',
        }}
      >
        {runs.length === 0 ? (
          <span style={{ fontSize: '16px', color: '#cbd5e1' }}>(表示項目 未設定)</span>
        ) : (
          runs.map((r, i) => (
            <div
              key={i}
              style={{
                fontSize: `${r.fontSize * PT_TO_PX}px`,
                fontWeight: r.bold ? 700 : 400,
                color: pptxColorToCss(r.color),
                lineHeight: 1.25,
                marginBottom: r.isTitle ? '10px' : '5px',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {r.text}
            </div>
          ))
        )}
      </div>

      {/* カテゴリバッジ (写真領域の左上) */}
      {badge && catLabel && (
        <div
          style={{
            position: 'absolute',
            left: `${px(badge.x)}px`,
            top: `${px(badge.y)}px`,
            minWidth: `${px(badge.w)}px`,
            height: `${px(badge.h)}px`,
            padding: '0 8px',
            background: badgeColor,
            color: '#ffffff',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: `${badgeFontPt * PT_TO_PX}px`,
            fontWeight: badgeStyle.bold ? 700 : 400,
            whiteSpace: 'nowrap',
            boxSizing: 'border-box',
          }}
        >
          {catLabel}
        </div>
      )}

      {/* QR (右下) */}
      {hasQr && qrDataUrl && (
        <div
          style={{
            position: 'absolute',
            left: `${px(qr.x)}px`,
            top: `${px(qr.y)}px`,
            width: `${px(qr.w)}px`,
            height: `${px(qr.h)}px`,
            background: '#ffffff',
            padding: '4px',
            boxSizing: 'border-box',
          }}
        >
          <img src={qrDataUrl} alt="" style={{ width: '100%', height: '100%' }} />
        </div>
      )}

      {/* ロゴ (左下、任意) */}
      {hasLogo && (
        <img
          src={theme.logoUrl}
          alt=""
          style={{
            position: 'absolute',
            left: `${px(logo.x)}px`,
            top: `${px(logo.y)}px`,
            width: `${px(logo.w)}px`,
            height: `${px(logo.h)}px`,
            objectFit: 'contain',
          }}
        />
      )}
    </div>
  );
}
